import { Rng, STREAM, hash32 } from './rng';
import type { Island, World } from './world';

/**
 * Wind gates: big rings hung over the best launches — dune crests, island rims facing the next
 * island. Threading one boosts your speed and shoves the storm back. They are the world's way of
 * saying "this way, hit this ramp".
 */
export interface Gate {
  id: number;
  x: number;
  y: number;
  z: number;
  /** Unit facing (the direction you fly through it). */
  nx: number;
  nz: number;
  r: number;
}

export class GateField {
  private readonly cache = new Map<number, Gate[]>();
  private readonly islandCache = new Map<number, Gate | null>();
  private readonly islScratch: Island[] = [];
  constructor(private readonly world: World) {}

  get cellSize(): number {
    return this.world.map.gates.cell;
  }

  cell(gi: number, gj: number): Gate[] {
    const key = hash32(gi, gj, 911);
    let list = this.cache.get(key);
    if (!list) {
      list = this.genDune(gi, gj, key);
      this.cache.set(key, list);
      if (this.cache.size > 2048) this.cache.clear();
    }
    return list;
  }

  /** Gates near (x, z): cell granularity on the dunes, per nearby island in the sky. */
  near(x: number, z: number, radius: number, out: Gate[]): Gate[] {
    out.length = 0;
    if (this.world.islands) {
      for (const isl of this.world.islandsNear(x, z, this.islScratch, radius)) {
        const g = this.islandGate(isl);
        if (g) out.push(g);
      }
      return out;
    }
    const C = this.cellSize;
    const i0 = Math.floor((x - radius) / C);
    const i1 = Math.floor((x + radius) / C);
    const j0 = Math.floor((z - radius) / C);
    const j1 = Math.floor((z + radius) / C);
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) for (const g of this.cell(i, j)) out.push(g);
    return out;
  }

  private genDune(gi: number, gj: number, key: number): Gate[] {
    const w = this.world;
    const def = w.map.gates;
    const C = def.cell;
    const rng = new Rng(w.seed, STREAM.props, key);
    if (!rng.chance(def.chance)) return [];
    const x = (gi + 0.2 + rng.float() * 0.6) * C;
    const z0 = (gj + 0.5) * C;
    if (x * x + z0 * z0 < 120 * 120) return [];
    // Walk north along x to find a crest with a real drop after it — a launch ramp.
    let best = -1;
    let bestZ = 0;
    for (let k = 0; k <= 60; k++) {
      const z = z0 + 60 - k * 2;
      const h = w.height(x, z);
      const hb = w.height(x, z + 2);
      const hf = w.height(x, z - 2);
      if (h < hb || h < hf) continue;
      const drop = h - w.height(x, z - 14);
      if (drop > best) {
        best = drop;
        bestZ = z;
      }
    }
    if (best < 2.5) return [];
    const yaw = (rng.float() - 0.5) * 0.5;
    let nx = yaw;
    let nz = -1;
    const l = Math.sqrt(nx * nx + nz * nz);
    nx /= l;
    nz /= l;
    const hz = bestZ - 8;
    return [{ id: key, x, y: w.height(x, bestZ) + 3.8, z: hz, nx, nz, r: def.radius }];
  }

  /** A gate on an island's rim, aimed at the next island in its chain. */
  private islandGate(isl: Island): Gate | null {
    const hit = this.islandCache.get(isl.id);
    if (hit !== undefined) return hit;
    const w = this.world;
    const def = w.map.gates;
    const rng = new Rng(w.seed, STREAM.props, hash32(isl.id, 912));
    let gate: Gate | null = null;
    const target = w.nextInChain(isl);
    let nx = target.cx - isl.cx;
    let nz = target.cz - isl.cz;
    const l = Math.sqrt(nx * nx + nz * nz);
    const gap = l - w.islandExtent(isl, nx / l, nz / l) - w.islandExtent(target, -nx / l, -nz / l);
    if (rng.chance(def.chance) && gap > 4) {
      nx /= l;
      nz /= l;
      // March out to the rim along that heading.
      let rx = isl.cx;
      let rz = isl.cz;
      for (let k = 0; k < 120; k++) {
        const tx = rx + nx * 1.5;
        const tz = rz + nz * 1.5;
        if (w.mask(tx, tz) < 0.5) break;
        rx = tx;
        rz = tz;
      }
      const lipH = w.height(rx - nx * 1.5, rz - nz * 1.5);
      gate = { id: hash32(isl.id, 913), x: rx + nx * 6, y: lipH + 4.4, z: rz + nz * 6, nx, nz, r: def.radius };
    }
    this.islandCache.set(isl.id, gate);
    return gate;
  }
}
